import React, { useState, useEffect } from 'react';
import intl from 'react-intl-universal';
import localData from 'utils/localData';
import api from 'utils/api';
import { Outlet, useNavigate } from 'react-router-dom';
// components and parts
import { PageLoader, PageError } from 'components';
import Modal from '@mui/material/Modal';
import { TABLE_TYPE } from 'components/PayrollTable/constants';
import { USER_DATA } from 'shared/constants/users';
import { DOLPHIN_API, KENSHIN_API } from 'shared/constants/apis';
import {
  getFirstDateOfTheSameMonth,
  getLastDateOfTheSameMonth,
} from 'utils/dateTime';
import { useSnackbar } from 'notistack';
import PayrollTable from 'components/PayrollTable';
import {
  getSummaryTableData,
  summaryTableColumns,
} from 'views/Billing/ReceivedBilling/summaryTable';
import {
  getDetailedTableData,
  detailsTableColumns,
} from 'views/Billing/ReceivedBilling/detailsTable';
import {
  getKenshinTableData,
  kenshinTableColumns,
} from 'views/Billing/ReceivedBilling/kenshinTable';
import InvoiceEditor from 'views/Billing/ReceivedBilling/InvoiceEditor';
import {
  updateBillForReceivedTasks,
  updateBillForReceivedTasksKenshin,
} from '../shared/api';
import SelectFeeAdjustment from 'components/PayrollTable/components/FeeAdjustment';
import { TABLE_STATE_KEYS } from 'shared/constants/tableOptions';
import logger from 'utils/logger';
import { isCurrentUserTaskManagerKenshin } from 'utils/privilege';
import { parseBillingMonth } from 'views/Billing/shared/utils';

const log = logger.createLogger('DashboardBillingForReceivedTasks');

const DashboardBillingForReceivedTasks = () => {
  const { enqueueSnackbar } = useSnackbar();

  const doesHaveKenshinPermission = isCurrentUserTaskManagerKenshin();

  const [loaded, setLoaded] = useState(false);
  const [loadedKenshin, setLoadedKenshin] = useState(
    doesHaveKenshinPermission ? false : true
  );
  const [ifError, setIfError] = useState(false);
  const navigate = useNavigate();

  const currentDate = new Date();
  const firstDate = getFirstDateOfTheSameMonth(currentDate);
  const lastDate = getLastDateOfTheSameMonth(currentDate);
  const [selectedStartDate, setSelectedStartDate] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.BILLING_START_DATE);
    return saved ? new Date(saved) : firstDate;
  });
  const [selectedEndDate, setSelectedEndDate] = useState(() => {
    const saved = sessionStorage.getItem(TABLE_STATE_KEYS.BILLING_END_DATE);
    return saved ? new Date(saved) : lastDate;
  });
  const [tableType, setTableType] = useState(TABLE_TYPE.SUMMARY);
  const [selectedSrcOrgId, setSelectedSrcOrgId] = useState(-1);
  const [selectedSrcOrgName, setSelectedSrcOrgName] = useState('');
  const [selectedBillingMonth, setSelectedBillingMonth] = useState('');
  const [billData, setBillData] = useState([]);
  const [billDataKenshin, setBillDataKenshin] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [columns, setColumns] = useState([]);

  // New code
  const [invoiceEditorModalOpen, setInvoiceEditorModalOpen] = useState(false);
  const [invoiceToBillDate, setInvoiceToBillDate] = useState('');
  const [invoiceToOrgId, setInvoiceToOrgId] = useState(-1);
  const [invoiceToOrgName, setInvoiceToOrgName] = useState('');
  const [invoiceTotalAmount, setInvoiceTotalAmount] = useState(0);
  const [updateApi, setUpdateApi] = useState(null);

  const dstOrgId = localData.get(USER_DATA.CURRENT_ORGANIZATION_ID);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.BILLING_START_DATE,
      selectedStartDate.toISOString()
    );
  }, [selectedStartDate]);

  useEffect(() => {
    sessionStorage.setItem(
      TABLE_STATE_KEYS.BILLING_END_DATE,
      selectedEndDate.toISOString()
    );
  }, [selectedEndDate]);

  useEffect(() => {
    const apiVariables = {
      params: {
        organizationId: parseInt(dstOrgId),
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    };

    const kenshinApiVariables = {
      baseURL: import.meta.env.VITE_KENSHIN_API_URL,
      params: {
        userId: localData.get(USER_DATA.USER_ID),
        username: localData.get(USER_DATA.USERNAME),
        organizationId: localData.get(USER_DATA.CURRENT_ORGANIZATION_ID),
        startDate: selectedStartDate,
        endDate: selectedEndDate,
      },
    };

    api
      .get(DOLPHIN_API.BILLS_RECEIVED_TASKS, apiVariables)
      .then(async (data) => {
        const { bills } = data;
        setBillData(bills);
        setLoaded(true);
      })
      .catch((error) => {
        log.error(error);
        setIfError(true);
      });

    if (doesHaveKenshinPermission) {
      api
        .get(KENSHIN_API.BILLS_RECEIVED_ISSUES, kenshinApiVariables)
        .then(async (data) => {
          const { bills, doctors } = data;
          setBillDataKenshin(bills);
          setDoctors(doctors);
          setLoadedKenshin(true);
        })
        .catch((error) => {
          log.error(error);
          setIfError(true);
        });
    }
  }, [dstOrgId, selectedStartDate, selectedEndDate]);

  useEffect(() => {
    if (loaded && loadedKenshin) {
      let columns = [];
      let data = [];
      if (tableType === TABLE_TYPE.SUMMARY) {
        columns = summaryTableColumns(
          setSelectedSrcOrgName,
          setSelectedSrcOrgId,
          setTableType,
          billData,
          billDataKenshin,
          setInvoiceToOrgId,
          setInvoiceToOrgName,
          setInvoiceTotalAmount,
          setInvoiceEditorModalOpen,
          doesHaveKenshinPermission,
          setSelectedBillingMonth,
          setInvoiceToBillDate
        );
        data = getSummaryTableData(billData, billDataKenshin);
        setUpdateApi(null);
      } else if (tableType === TABLE_TYPE.DETAILED) {
        columns = detailsTableColumns(openSeimitsuIssue);
        data = getDetailedTableData(
          billData,
          selectedSrcOrgId,
          selectedBillingMonth
        );
        setUpdateApi(() => updateBillForReceivedTasks);
      } else if (tableType === TABLE_TYPE.DETAILED_KENSHIN) {
        columns = kenshinTableColumns(openKenshinIssue);
        data = getKenshinTableData(
          billDataKenshin,
          selectedSrcOrgId,
          selectedBillingMonth
        );
        setUpdateApi(() => updateBillForReceivedTasksKenshin);
      }
      setTableData(data);
      setColumns(columns);
    }
  }, [loaded, loadedKenshin, tableType, billData, billDataKenshin]);

  if (!(loaded && loadedKenshin)) {
    return <PageLoader />;
  }
  if (ifError) {
    return <PageError />;
  }

  const refreshTableData = () => {
    if (selectedEndDate.toDateString() === new Date().toDateString()) {
      setSelectedEndDate(new Date());
    }
    enqueueSnackbar(intl.get('shared_table_data_reload_success'), {
      variant: 'success',
      autoHideDuration: 3000,
    });
  };

  const openSeimitsuIssue = (issueId) => {
    navigate(`/dashboard/billing/received-billing/seimitsu/issues/${issueId}`);
  };

  const openKenshinIssue = (issueId) => {
    navigate(`/dashboard/billing/received-billing/kenshin/issues/${issueId}`);
  };

  const SelectFeeAdjustmentComponent = (table) => {
    return (
      <SelectFeeAdjustment
        table={table}
        refreshTableData={refreshTableData}
        updateStatus={updateApi}
      />
    );
  };

  return (
    <div>
      <PayrollTable
        columns={columns}
        data={tableData}
        tableTitle={intl.get('billing_for_received_cases_text_received_cases')}
        timeRangeStart={selectedStartDate}
        timeRangeEnd={selectedEndDate}
        updateTimeRangeStart={setSelectedStartDate}
        updateTimeRangeEnd={setSelectedEndDate}
        handleRefreshClick={refreshTableData}
        subtitle={`[${parseBillingMonth(
          selectedBillingMonth
        )}] ${selectedSrcOrgName}`}
        setTableType={setTableType}
        tableType={tableType}
        multiSelectComponent={SelectFeeAdjustmentComponent}
        enableRowSelection={false}
      />

      <Outlet
        context={{ doctors: doctors, updateLocalProjectIssues: () => {} }}
      />

      <Modal
        open={invoiceEditorModalOpen}
        onClose={() => setInvoiceEditorModalOpen(false)}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <div>
          <InvoiceEditor
            invoiceToBillDate={invoiceToBillDate}
            billData={billData}
            billDataKenshin={billDataKenshin}
            invoiceToOrgId={invoiceToOrgId}
            invoiceToOrgName={invoiceToOrgName}
            invoiceTotalAmount={invoiceTotalAmount}
            modalClose={() => setInvoiceEditorModalOpen(false)}
          />
        </div>
      </Modal>
    </div>
  );
};

export default DashboardBillingForReceivedTasks;
